import { SxProps, Theme } from '@mui/material';

// Simplified color palette - only primary and black
const colors = {
  primary: '#205B56',
  primaryLight: '#2d7a73',
  primaryDark: '#164039',
  text: '#000',
  background: '#f8fafc',
  cardBg: '#ffffff',
  border: '#e2e8f0',
};

export const styles = {
  container: {
    py: 4,
    minHeight: '100vh',
    background: `#fafafa`,
  } as SxProps<Theme>,

  title: {
    fontWeight: 700,
    color: colors.text,
    mb: 2,
    fontSize: { xs: '1.25rem', md: '2.25rem' },
  } as SxProps<Theme>,

  bigNumbersGrid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, 1fr)',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(4, 1fr)',
    },
    gap: { xs: 1.5, sm: 2, md: 3 },
    mb: 4,
  } as SxProps<Theme>,

  bigNumberCard: {
    backgroundColor: colors.cardBg,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    borderRadius: 3,
    border: `1px solid ${colors.border}`,
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: colors.primary,
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    '@media (max-width: 600px)': {
      '& .MuiCardContent-root': {
        padding: '12px',
      },
    },
  } as SxProps<Theme>,

  bigNumberLabel: {
    color: colors.text,
    fontWeight: 500,
    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
    mb: { xs: 0.5, md: 1.5 },
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.5,
    lineHeight: 1.3,
  } as SxProps<Theme>,

  bigNumberIcon: {
    fontSize: { xs: '1rem', md: '1.25rem' },
    color: colors.primary,
  } as SxProps<Theme>,

  bigNumberValue: {
    color: colors.text,
    fontWeight: 500,
    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
  } as SxProps<Theme>,

  filtersContainer: {
    mb: 4,
  } as SxProps<Theme>,

  filterLabel: {
    color: colors.text,
    fontWeight: 500,
    mb: 2,
    fontSize: '1rem',
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  } as SxProps<Theme>,

  dateFiltersBox: {
    display: 'flex',
    gap: { xs: 1, sm: 1.5, md: 2 },
    mb: 1,
    flexWrap: 'wrap',
  } as SxProps<Theme>,

  filterDivider: {
    my: 3,
    borderColor: colors.border,
    opacity: 0.6,
  } as SxProps<Theme>,

  dateFilterButton: (isActive: boolean) =>
  ({
    backgroundColor: isActive ? colors.primary : colors.cardBg,
    color: isActive ? '#fff' : colors.text,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
    boxShadow: isActive
      ? '0 2px 4px -1px rgba(32, 91, 86, 0.3)'
      : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    borderRadius: 1.5,
    px: { xs: 1.5, sm: 2, md: 2.5 },
    py: { xs: 0.75, sm: 1, md: 1 },
    minWidth: 'auto',
    border: `1px solid ${isActive ? colors.primary : colors.border}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: isActive ? colors.primaryDark : colors.background,
      boxShadow: isActive
        ? '0 4px 6px -1px rgba(32, 91, 86, 0.4)'
        : '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
    },
  } as SxProps<Theme>),

  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: colors.cardBg,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: colors.border,
        borderWidth: '1px',
      },
      '&:hover': {
        backgroundColor: colors.background,
        '& fieldset': {
          borderColor: colors.primary,
        },
      },
      '&.Mui-focused': {
        backgroundColor: colors.cardBg,
        boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.1)`,
        '& fieldset': {
          borderColor: colors.primary,
          borderWidth: '2px',
        },
      },
    },
    '& .MuiInputBase-input': {
      color: colors.text,
      fontSize: '0.95rem',
    },
    '& .MuiInputBase-input::placeholder': {
      color: colors.text,
      opacity: 0.7,
    },
  } as SxProps<Theme>,

  alert: {
    borderRadius: 2,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)',
  } as SxProps<Theme>,
};
